import React, { useState, useEffect } from 'react'
import { getLectureAttendance, saveLectureAttendance } from '../api/lectureApi'
import { appToast } from '../../../../components/AppToast'

export default function LectureAttendanceModal({ isOpen, onClose, lectureId, onSuccess }) {
  const [loading, setLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [meta, setMeta] = useState(null)
  const [students, setStudents] = useState([])

  useEffect(() => {
    if (!isOpen || !lectureId) return

    async function loadData() {
      try {
        setLoading(true)
        const res = await getLectureAttendance(lectureId)
        setMeta(res)
        setStudents(res?.students || [])
      } catch (err) {
        console.error('Failed to load lecture attendance roster:', err)
        appToast.error('Failed to load attendance roster for this lecture.')
      } finally {
        setLoading(false)
      }
    }

    loadData()
  }, [isOpen, lectureId])

  if (!isOpen) return null

  const handleToggleAttendance = (studentId) => {
    setStudents((prev) =>
      prev.map((s) => {
        if (s.studentId === studentId) {
          const next = s.attendance === 'P' ? 'A' : 'P'
          return { ...s, attendance: next }
        }
        return s
      })
    )
  }

  const handleMarkAll = (status) => {
    setStudents((prev) => prev.map((s) => ({ ...s, attendance: status })))
  }

  const handleSave = async () => {
    try {
      setIsSaving(true)
      const records = students.map((s) => ({
        studentId: s.studentId,
        attendance: s.attendance || 'A',
      }))
      const res = await saveLectureAttendance(lectureId, records)
      appToast.success(res?.message || 'Attendance saved successfully!')
      onSuccess?.()
      onClose()
    } catch (err) {
      console.error('Failed to save lecture attendance:', err)
      const msg = err?.response?.data?.detail || err?.message || 'Failed to save attendance.'
      appToast.error(msg)
    } finally {
      setIsSaving(false)
    }
  }

  const presentCount = students.filter((s) => s.attendance === 'P').length
  const totalCount = students.length

  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/50 p-4 backdrop-blur-xs animate-in fade-in duration-200">
      <div
        className="relative flex max-h-[90vh] w-full max-w-3xl flex-col rounded-3xl border border-outline-variant/30 bg-surface-container-lowest shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-outline-variant/20 px-6 py-4 bg-surface-container-low/40">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-primary/10 text-primary">
              <span className="material-symbols-outlined text-2xl">how_to_reg</span>
            </div>
            <div>
              <h3 className="font-display text-base font-bold text-on-surface">
                Lecture Attendance
              </h3>
              <p className="text-xs text-on-surface-variant font-medium">
                {meta?.subject_name ? `${meta.subject_name} • ` : ''}
                {meta?.course_name ? `${meta.course_name} • ` : ''}
                {meta?.lecture_date || 'Today'}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded-xl text-on-surface-variant hover:bg-surface-container-high hover:text-on-surface transition cursor-pointer"
          >
            <span className="material-symbols-outlined text-lg">close</span>
          </button>
        </div>

        {/* Toolbar & Summary */}
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-outline-variant/15 px-6 py-3 bg-surface-container-lowest">
          <div className="flex items-center gap-2 text-xs font-semibold text-on-surface">
            <span>Summary:</span>
            <span className="rounded-full bg-emerald-50 px-2.5 py-0.5 text-xs font-bold text-emerald-700 ring-1 ring-emerald-200">
              {presentCount} Present
            </span>
            <span className="rounded-full bg-rose-50 px-2.5 py-0.5 text-xs font-bold text-rose-700 ring-1 ring-rose-200">
              {totalCount - presentCount} Absent
            </span>
            <span className="text-on-surface-variant text-xs">
              ({totalCount > 0 ? Math.round((presentCount / totalCount) * 100) : 0}% Attendance)
            </span>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => handleMarkAll('P')}
              className="rounded-xl border border-emerald-300 bg-emerald-50 px-3 py-1 text-xs font-bold text-emerald-700 hover:bg-emerald-100 transition cursor-pointer"
            >
              Mark All Present
            </button>
            <button
              type="button"
              onClick={() => handleMarkAll('A')}
              className="rounded-xl border border-rose-300 bg-rose-50 px-3 py-1 text-xs font-bold text-rose-700 hover:bg-rose-100 transition cursor-pointer"
            >
              Mark All Absent
            </button>
          </div>
        </div>

        {/* Students Table */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {loading ? (
            <div className="flex h-48 flex-col items-center justify-center gap-3 text-on-surface-variant">
              <span className="material-symbols-outlined text-3xl animate-spin text-primary">
                progress_activity
              </span>
              <span className="text-xs font-medium">Loading student roster...</span>
            </div>
          ) : students.length === 0 ? (
            <div className="flex h-48 flex-col items-center justify-center gap-2 text-on-surface-variant">
              <span className="material-symbols-outlined text-3xl text-outline-variant">
                person_off
              </span>
              <span className="text-xs font-bold">No students found for this lecture's course/batch.</span>
            </div>
          ) : (
            <div className="overflow-hidden rounded-2xl border border-outline-variant/30">
              <table className="w-full text-left text-xs">
                <thead className="border-b border-outline-variant/20 bg-surface-container-low text-[11px] font-bold uppercase tracking-wider text-on-surface-variant">
                  <tr>
                    <th className="px-4 py-2.5">Roll No</th>
                    <th className="px-4 py-2.5">Student Name</th>
                    <th className="px-4 py-2.5">Branch</th>
                    <th className="px-4 py-2.5">Batch</th>
                    <th className="px-4 py-2.5 text-center">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-outline-variant/15">
                  {students.map((student) => {
                    const isPresent = student.attendance === 'P'
                    return (
                      <tr
                        key={student.studentId}
                        onClick={() => handleToggleAttendance(student.studentId)}
                        className="cursor-pointer transition hover:bg-surface-container-low/50"
                      >
                        <td className="px-4 py-2.5 font-mono font-medium text-on-surface">
                          {student.rollNo || '—'}
                        </td>
                        <td className="px-4 py-2.5 font-semibold text-on-surface">
                          {student.studentName}
                        </td>
                        <td className="px-4 py-2.5 text-on-surface-variant">
                          {student.branchName || '—'}
                        </td>
                        <td className="px-4 py-2.5 text-on-surface-variant">
                          {student.batchName || '—'}
                        </td>
                        <td className="px-4 py-2.5 text-center">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation()
                              handleToggleAttendance(student.studentId)
                            }}
                            className={`inline-flex items-center gap-1 rounded-xl px-3 py-1 text-xs font-bold shadow-2xs transition cursor-pointer ${
                              isPresent
                                ? 'bg-emerald-600 text-white hover:bg-emerald-700'
                                : 'bg-rose-100 text-rose-700 hover:bg-rose-200'
                            }`}
                          >
                            <span className="material-symbols-outlined text-sm">
                              {isPresent ? 'check_circle' : 'cancel'}
                            </span>
                            {isPresent ? 'Present' : 'Absent'}
                          </button>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 border-t border-outline-variant/20 px-6 py-4 bg-surface-container-low/30">
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl border border-outline-variant/30 bg-surface-container-low px-5 py-2 text-xs font-bold text-on-surface hover:bg-surface-container-high transition cursor-pointer"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={isSaving || students.length === 0}
            className="flex items-center gap-2 rounded-xl bg-primary px-6 py-2 text-xs font-bold text-on-primary hover:bg-primary/90 shadow-xs transition disabled:opacity-50 cursor-pointer"
          >
            {isSaving ? (
              <>
                <span className="material-symbols-outlined text-sm animate-spin">
                  progress_activity
                </span>
                <span>Saving...</span>
              </>
            ) : (
              <>
                <span className="material-symbols-outlined text-sm">save</span>
                <span>Save Attendance</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}

